import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit2,
  Trash2,
  Shield,
  Ban,
  Mail,
  MapPin,
  Calendar,
  User,
  MessageSquare,
  ExternalLink,
} from 'lucide-react';
import {
  AdminClientApi,
  ClientUserInterface,
  GetUserResponseInterface,
} from '../../Admin-ClientLibrary';
import BeliefsListView from '../../components/BeliefsListView';
import { getApiUrl } from '../../config/api';
import { useRecentEntities } from '../../contexts/RecentEntitiesContext';
import './UserDetail.css';

// Initialize the Admin Client Library
const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<ClientUserInterface | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { addRecentEntity } = useRecentEntities();

  useEffect(() => {
    if (id) {
      fetchUser(parseInt(id));
    }
  }, [id]);

  const fetchUser = async (userId: number): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response = await adminApi.getUser({ userId });

      if (response.success && response.user) {
        setUser(response.user);
        addRecentEntity('user', response.user);
      } else {
        setError(response.message || 'User not found');
      }
    } catch (err: any) {
      console.error('Error fetching user:', err);
      setError(err.message || 'Failed to fetch user');
      // Fallback to mock data
      setUser(getMockUser(userId));
    } finally {
      setLoading(false);
    }
  };

  const getMockUser = (userId: number): ClientUserInterface => ({
    account_id: userId,
    name: `User ${userId}`,
    email: `user${userId}@example.com`,
    type: 'premium',
    continent: 'North America',
    country: 'United States',
    state: 'New York',
    city: 'New York',
    sex: userId % 2 === 0 ? 'M' : 'F',
    religion: 'Christian',
    political: 'Central',
    birthdate: new Date('1990-05-15'),
    education: 'Bachelors',
    createdAt: new Date('2023-01-15'),
    updatedAt: new Date('2023-01-15'),
    deletedAt: new Date('2023-01-15'),
    ageCategory: 'Adult',
  });

  const getRoleColor = (type: string): string => {
    switch (type) {
      case 'premium':
        return 'premium';
      case 'beta':
        return 'beta';
      case 'free':
        return 'free';
      default:
        return 'free';
    }
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatLocation = (user: ClientUserInterface): string => {
    const locationParts = [];
    if (user.city) locationParts.push(user.city);
    if (user.state) locationParts.push(user.state);
    if (user.country) locationParts.push(user.country);
    return locationParts.length > 0 ? locationParts.join(', ') : 'Not specified';
  };

  const handleViewThoughts = (): void => {
    if (user) {
      navigate(`/thoughts?page=1&authorId=${user.account_id}`);
    }
  };

  const handleViewBeliefs = (): void => {
    if (user) {
      navigate(`/beliefs?page=1&userId=${user.account_id}`);
    }
  };

  if (loading) {
    return (
      <div className="user-detail-page">
        <div className="loading">Loading user details...</div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="user-detail-page">
        <div className="error-message">
          <p>Error: {error}</p>
          <button onClick={() => navigate('/users')} className="btn-secondary">
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-detail-page">
        <div className="error-message">
          <p>User not found</p>
          <button onClick={() => navigate('/users')} className="btn-secondary">
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-detail-page">
      <div className="user-detail-header">
        <button onClick={() => navigate('/users')} className="back-button">
          <ArrowLeft size={16} />
          Back to Users
        </button>

        <div className="user-detail-title">
          <div className="user-avatar-large">{user.name.charAt(0).toUpperCase()}</div>
          <div className="user-title-info">
            <h1>{user.name}</h1>
            <span className={`role-badge ${getRoleColor(user.type)}`}>{user.type}</span>
          </div>
        </div>

        <div className="user-actions">
          <button className="btn-secondary">
            <Edit2 size={16} />
            Edit User
          </button>
          <button className="btn-secondary">
            <Ban size={16} />
            Suspend
          </button>
          <button className="btn-danger">
            <Trash2 size={16} />
            Delete
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <p>Note: Could not fetch from API, showing mock data</p>
        </div>
      )}

      <div className="user-detail-content">
        <div className="user-info-grid">
          <div className="info-card">
            <div className="info-card-header">
              <Mail size={20} />
              <h3>Contact Information</h3>
            </div>
            <div className="info-card-content">
              <div className="info-row">
                <span className="info-label">Email:</span>
                <span className="info-value">{user.email}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Location:</span>
                <span className="info-value">{formatLocation(user)}</span>
              </div>
            </div>
          </div>

          <div className="info-card">
            <div className="info-card-header">
              <User size={20} />
              <h3>Account Details</h3>
            </div>
            <div className="info-card-content">
              <div className="info-row">
                <span className="info-label">User ID:</span>
                <span className="info-value">{user.account_id}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Account Type:</span>
                <span className="info-value">
                  <span className={`role-badge ${getRoleColor(user.type)}`}>{user.type}</span>
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Age Category:</span>
                <span className="info-value">{user.ageCategory}</span>
              </div>
              {user.birthdate && (
                <div className="info-row">
                  <span className="info-label">Birth Date:</span>
                  <span className="info-value">{formatDate(user.birthdate)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="info-card">
            <div className="info-card-header">
              <User size={20} />
              <h3>Personal Information</h3>
            </div>
            <div className="info-card-content">
              <div className="info-row">
                <span className="info-label">Gender:</span>
                <span className="info-value">
                  {user.sex === 'M' ? 'Male' : user.sex === 'F' ? 'Female' : 'Not specified'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Religion:</span>
                <span className="info-value">{user.religion || 'Not specified'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Political:</span>
                <span className="info-value">{user.political || 'Not specified'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Education:</span>
                <span className="info-value">{user.education || 'Not specified'}</span>
              </div>
              {user.continent && (
                <div className="info-row">
                  <span className="info-label">Continent:</span>
                  <span className="info-value">{user.continent}</span>
                </div>
              )}
            </div>
          </div>

          <div className="info-card">
            <div className="info-card-header">
              <Calendar size={20} />
              <h3>Timeline</h3>
            </div>
            <div className="info-card-content">
              <div className="info-row">
                <span className="info-label">Joined:</span>
                <span className="info-value">{formatDate(user.createdAt)}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Last Updated:</span>
                <span className="info-value">{formatDate(user.updatedAt)}</span>
              </div>
              {user.deletedAt && (
                <div className="info-row">
                  <span className="info-label">Deleted:</span>
                  <span className="info-value">{formatDate(user.deletedAt)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Cards for User Content */}
        <div className="user-content-navigation">
          <div className="navigation-card" onClick={handleViewThoughts}>
            <div className="navigation-card-header">
              <MessageSquare size={24} />
              <h3>User Thoughts</h3>
            </div>
            <div className="navigation-card-content">
              <p>View and search through all thoughts created by this user</p>
              <div className="navigation-card-action">
                <span>View Thoughts</span>
                <ExternalLink size={16} />
              </div>
            </div>
          </div>
        </div>

        {/* User's Beliefs Section */}
        <div className="user-beliefs-section">
          <div className="user-beliefs-header">
            <h3>User's Beliefs</h3>
            <button className="btn-secondary view-all-btn" onClick={handleViewBeliefs}>
              <ExternalLink size={14} />
              View All Beliefs
            </button>
          </div>
          <BeliefsListView
            filters={{ userId: user.account_id }}
            showCheckboxes={false}
            showBulkActions={false}
            compact={false}
            limit={8}
          />
        </div>
      </div>
    </div>
  );
};

export default UserDetail;
